import React, { useEffect, useState } from 'react';
import { getWorkersHealth } from '../../services/syncApi';
import { DEFAULT_ENVIRONMENT, workerKey } from '../../constants/markets';

const WORKER_POLL_MS = 5000;

const fmtAge = (s) => {
  if (s == null) return 'never';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};

/**
 * Shows whether a sync worker is alive for `marketCode`. Polls
 * /workers/health every 5s so a freshly-started worker is detected
 * without a page refresh.
 *
 * Variants:
 *  - variant="full"  (default) — used on JobDetail; explains why a job
 *                                 isn't progressing and shows the start
 *                                 command verbatim.
 *  - variant="chip" — compact one-liner suitable for tables (Manual Sync
 *                     results).
 */
const WorkerStatusBanner = ({ marketCode, environment = DEFAULT_ENVIRONMENT, variant = 'full' }) => {
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const h = await getWorkersHealth();
        if (!cancelled) setHealth(h);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    };
    tick();
    const id = window.setInterval(tick, WORKER_POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [marketCode, environment]);

  // Composite key matches the backend worker-health response. Fall back to a
  // bare market lookup for backward compatibility with any older response.
  const label = environment === DEFAULT_ENVIRONMENT ? marketCode : `${environment} ${marketCode}`;

  if (err) return null; // Don't block the page if /workers/health 401s; banner is a hint.
  if (!health) return null;
  const allMarkets = health.markets || {};
  const m = allMarkets[workerKey(environment, marketCode)] || allMarkets[marketCode];
  if (!m) return null;

  if (variant === 'chip') {
    if (m.alive) {
      return (
        <span className="worker-chip alive" title={`worker ${m.worker_id} last seen ${fmtAge(m.seconds_since_heartbeat)}`}>
          worker alive
        </span>
      );
    }
    return (
      <span className="worker-chip dead" title={m.last_seen_at ? `last seen ${fmtAge(m.seconds_since_heartbeat)}` : 'no worker has ever started for this market'}>
        no {label} worker
      </span>
    );
  }

  // full
  if (m.alive) {
    return (
      <div className="worker-banner ok">
        <strong>{label} worker is alive</strong> — id <code>{m.worker_id}</code>,
        last heartbeat {fmtAge(m.seconds_since_heartbeat)}.
        {m.pending_jobs > 0 && <> Queue depth: <strong>{m.pending_jobs}</strong> pending.</>}
      </div>
    );
  }

  const lastSeen = m.last_seen_at
    ? <>last heartbeat <strong>{fmtAge(m.seconds_since_heartbeat)}</strong> (grace {health.liveness_grace_seconds}s).</>
    : <>no <code>{label}</code> worker has ever heartbeat. Has one been started in this session?</>;

  return (
    <div className="worker-banner error" role="alert">
      <strong>⚠ No active worker for {label}.</strong>{' '}
      Jobs queued for this market will stay <span className="status status-pending">pending</span> until a worker is running. {lastSeen}
      {' '}Start the supervisor to bring up all workers (prod AU/NZ + staging AU/NZ):
      <pre className="worker-cmd">
{`cd /Users/michaelkaminski/Documents/Projects/Waterco
PYTHONPATH=src .venv/bin/python -m waterco.worker_manager`}
      </pre>
      {m.pending_jobs > 0 && (
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          {m.pending_jobs} {label} job{m.pending_jobs === 1 ? '' : 's'} waiting in queue.
        </p>
      )}
    </div>
  );
};

export default WorkerStatusBanner;
